import type { AxiosRequestConfig } from 'axios';

import {
  type CamelizedType,
  transformObjectToCamelCase,
  transformObjectToSnakeCase,
} from '@utilities';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const transformPlainObjectOrArrayToSnakeCase = (value: unknown): unknown => {
  if (Array.isArray(value) || isPlainObject(value)) {
    return transformObjectToSnakeCase(value);
  }

  return value;
};

/**
 * Normalizes Axios request data and params from frontend camelCase to API snake_case.
 * Non-plain request bodies, such as FormData, are passed through unchanged.
 *
 * @example
 * axios(createApiRequestConfig({
 *   method: 'get',
 *   params: { instantSurveyTypeId: 'standard-v3' },
 *   url: '/instantsurvey/surveytemplate',
 * }));
 */
export const createApiRequestConfig = <TConfig extends AxiosRequestConfig>(
  config: TConfig
): TConfig => ({
  ...config,
  data: transformPlainObjectOrArrayToSnakeCase(config.data),
  params: transformPlainObjectOrArrayToSnakeCase(config.params),
});

/**
 * Normalizes API response data from snake_case to frontend camelCase.
 * Prefer this over using transformObjectToCamelCase directly in API helpers so the return type
 * preserves the camelized shape of the response payload.
 *
 * @example
 * const questionBank = transformApiResponseData({
 *   created_at: '2026-06-30T01:21:54Z',
 *   screener_label: 'New Buyers',
 * });
 * // questionBank.createdAt
 * // questionBank.screenerLabel
 */
export function transformApiResponseData<TData>(data: TData): CamelizedType<TData> {
  return transformObjectToCamelCase(data) as CamelizedType<TData>;
}
